// Badge.jsx - Professional badge component for Foundations Risk Aperture
import React from 'react';

const Badge = ({ 
  children, 
  variant = 'default',
  size = 'md',
  icon,
  pulse = false,
  className = '',
  ...props 
}) => {
  const baseClasses = 'inline-flex items-center gap-2 font-semibold tracking-wide relative overflow-hidden rounded-full border';
  
  const variantClasses = {
    default: 'bg-[#172a45] text-[#ccd6f6] border-[rgba(255,255,255,0.12)]',
    primary: 'bg-blue-100 text-blue-700 border-blue-200',
    success: 'bg-green-100 text-green-700 border-green-200',
    warning: 'bg-yellow-100 text-yellow-700 border-yellow-200', 
    danger: 'bg-red-100 text-red-700 border-red-200',
    complete: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    info: 'bg-purple-100 text-purple-700 border-purple-200',
    // Risk-specific variants
    'low-risk': 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'medium-risk': 'bg-amber-50 text-amber-700 border-amber-200',
    'high-risk': 'bg-red-50 text-red-700 border-red-200',
    'critical-risk': 'bg-red-100 text-[#ccd6f6] border-red-300',
    // Performance variants
    'above-average': 'bg-green-50 text-green-700 border-green-200',
    'below-average': 'bg-orange-50 text-orange-700 border-orange-200',
    'industry-standard': 'bg-blue-50 text-blue-700 border-blue-200'
  };
  
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-xs',
    lg: 'px-4 py-2 text-sm',
    xl: 'px-5 py-2.5 text-base'
  };
  
  const pulseClasses = pulse ? 'animate-pulse' : '';
  
  const combinedClasses = [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    pulseClasses,
    className
  ].filter(Boolean).join(' ');

  return (
    <span 
      className={combinedClasses}
      {...props}
    >
      {/* Animated background for hover effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] hover:translate-x-[100%] transition-transform duration-500 ease-out" />
      
      {/* Badge content */}
      <div className="relative z-10 flex items-center gap-1.5">
        {icon && <span className="text-current">{icon}</span>}
        <span>{children}</span>
      </div>
    </span>
  );
};

// Specialized badge components for risk assessment
export const RiskBadge = ({ level, className = '', ...props }) => {
  const riskMappings = {
    'Very Low': { variant: 'low-risk', icon: '🟢' },
    'Low': { variant: 'low-risk', icon: '🟢' },
    'Medium': { variant: 'medium-risk', icon: '🟡' },
    'High': { variant: 'high-risk', icon: '🔴' },
    'Very High': { variant: 'critical-risk', icon: '⚠️' },
    'Critical': { variant: 'critical-risk', icon: '🚨' }
  };
  
  const mapping = riskMappings[level] || { variant: 'default', icon: '⚪' };
  
  return (
    <Badge 
      variant={mapping.variant}
      icon={mapping.icon}
      className={className}
      {...props}
    >
      {level} Risk
    </Badge>
  );
};

export const PerformanceBadge = ({ performance, className = '', ...props }) => {
  const performanceMappings = {
    'Well Above Industry Standard': { variant: 'above-average', icon: '⭐' },
    'Above Industry Standard': { variant: 'above-average', icon: '📈' },
    'Within Industry Standard': { variant: 'industry-standard', icon: '📊' },
    'Below Industry Standard': { variant: 'below-average', icon: '📉' },
    'Well Below Industry Standard': { variant: 'below-average', icon: '⚠️' }
  };
  
  const mapping = performanceMappings[performance] || { variant: 'default', icon: '📊' };
  
  return (
    <Badge 
      variant={mapping.variant}
      icon={mapping.icon}
      className={className}
      {...props}
    >
      {performance.replace(' Industry Standard', '')}
    </Badge>
  );
};

export const StatusBadge = ({ status, className = '', ...props }) => {
  const statusMappings = {
    'Complete': { variant: 'complete', icon: '✅' },
    'In Progress': { variant: 'warning', icon: '🔄' },
    'Pending': { variant: 'default', icon: '⏳' },
    'Failed': { variant: 'danger', icon: '❌' },
    'Active': { variant: 'success', icon: '🟢' },
    'Inactive': { variant: 'default', icon: '⚪' },
    'Technology': { variant: 'info', icon: '💻' }
  };
  
  const mapping = statusMappings[status] || { variant: 'default', icon: '⚪' };
  
  return (
    <Badge 
      variant={mapping.variant}
      icon={mapping.icon}
      className={className}
      {...props}
    >
      {status}
    </Badge>
  );
};

// Priority badge for recommendations
export const PriorityBadge = ({ priority, className = '', ...props }) => {
  const priorityMappings = {
    'CRITICAL': { variant: 'critical-risk', icon: '🚨', pulse: true },
    'URGENT': { variant: 'high-risk', icon: '⚠️' },
    'HIGH': { variant: 'medium-risk', icon: '🔴' },
    'MEDIUM': { variant: 'warning', icon: '🟡' },
    'IMPROVE': { variant: 'medium-risk', icon: '📈' },
    'OPTIMIZE': { variant: 'warning', icon: '⚙️' },
    'MAINTAIN': { variant: 'industry-standard', icon: '✅' }
  };
  
  const mapping = priorityMappings[priority] || { variant: 'default', icon: '⚪' };
  
  return (
    <Badge 
      variant={mapping.variant}
      icon={mapping.icon}
      pulse={mapping.pulse}
      className={className}
      {...props}
    >
      {priority}
    </Badge>
  );
};

export { Badge };